import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Upload, Trash2, Download, File, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GeneralDocuments() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/documents`,
        { withCredentials: true }
      );
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File size must be less than 20MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.title.trim()) {
      toast.error('Please provide a title and select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('file', uploadForm.file);

      await axios.post(
        `${BACKEND_URL}/api/documents`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success('Document uploaded successfully');
      setUploadForm({ title: '', description: '', file: null });
      document.getElementById('file-input').value = '';
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/documents/${docId}`,
        { withCredentials: true }
      );
      toast.success('Document deleted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const canUpload = user?.role === 'admin' || user?.role === 'teacher';

  return (
    <div data-testid="general-documents-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          General Documents
        </h1>
        <p className="text-[#64748B] mt-2">Share and access school-wide documents</p>
      </div>

      {/* Upload Form - Only for Admin and Teachers */}
      {canUpload && (
        <Card className="mb-8 shadow-lg border-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#667EEA]" />
              Upload New Document
            </CardTitle>
            <CardDescription>Upload documents to share with all staff and students</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="title">Document Title *</Label>
                <Input
                  id="title"
                  data-testid="document-title-input"
                  placeholder="e.g., School Calendar 2026"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  data-testid="document-description-input"
                  placeholder="Brief description of the document..."
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="file-input">Select File * (Max 20MB)</Label>
                <Input
                  id="file-input"
                  type="file"
                  data-testid="document-file-input"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.csv,.txt"
                  required
                />
                {uploadForm.file && (
                  <p className="text-sm text-[#64748B] mt-2">
                    Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                  </p>
                )}
              </div>

              <Button
                type="submit"
                data-testid="upload-document-btn"
                className="w-full bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
                disabled={uploading}
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Documents List */}
      <Card className="shadow-lg border-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-[#667EEA]" />
            Available Documents
          </CardTitle>
          <CardDescription>All shared documents accessible to staff and students</CardDescription>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12 text-[#64748B]" data-testid="no-documents-message">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No documents available yet</p>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} data-testid="document-row">
                      <TableCell className="font-medium">{doc.title}</TableCell>
                      <TableCell className="text-[#64748B] max-w-xs truncate">
                        {doc.description || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <File className="w-4 h-4 text-[#667EEA]" />
                          <div>
                            <p className="text-sm font-medium">{doc.original_filename}</p>
                            <p className="text-xs text-[#64748B]">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{doc.uploaded_by}</p>
                          <p className="text-xs text-[#64748B]">{doc.uploaded_by_role}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#64748B]">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <a
                            href={`${BACKEND_URL}/api/documents/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button size="sm" variant="outline" data-testid="download-document-btn">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          {(user.role === 'admin' || doc.uploaded_by === user.name) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(doc.id)}
                              data-testid="delete-document-btn"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
